import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useFiltersStore } from '@/store/use-filters-store';
import { useUserInfoStore } from '@/store/use-user-info-store';

export const useFilterParams = (filterNames: string[]) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedAccount } = useUserInfoStore();
  const { filters, setFilters, clearFilters, syncWithUrl } = useFiltersStore();
  const location = useLocation();

  const accountCode = selectedAccount?.code;
  const page = location.pathname;

  const urlFilters = filterNames.reduce((acc, name) => {
    acc[name] = searchParams.get(name) || '';
    return acc;
  }, {} as Record<string, string>);

  const storedFilters = filters[accountCode]?.[page] || {};

  const hasUrlFilters = filterNames.some((name) => Boolean(urlFilters[name]));

  useEffect(() => {
    if (!accountCode) {
      return;
    }

    if (hasUrlFilters) {
      syncWithUrl(page, urlFilters);
    } else {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(storedFilters).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        }
      });
      setSearchParams(newParams);
    }
  }, [location.pathname, accountCode]);

  useEffect(() => {
    if (!accountCode) {
      return;
    }

    if (hasUrlFilters) {
      syncWithUrl(page, urlFilters);
    }
  }, [searchParams, accountCode]);

  const setFilterValue = (name: string, value: string) => {
    if (!accountCode) {
      return;
    }

    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(name, value);
    } else {
      newParams.delete(name);
    }
    setSearchParams(newParams);
  };

  const setFilterValues = (updates: Record<string, string>) => {
    if (!accountCode) {
      return;
    }

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      return newParams;
    });
  };

  const clearAllFilters = () => {
    if (!accountCode) {
      return;
    }

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      filterNames.forEach((name) => newParams.delete(name));
      return newParams;
    });

    clearFilters(page);
  };

  const hasFilters = filterNames.some((name) => Boolean(urlFilters[name]));

  return { 
    filters: urlFilters, 
    setFilterValue, 
    clearAllFilters, 
    setFilterValues, 
    hasFilters 
  };
};
